import styled from "styled-components";

const Outer = styled.div`
  margin-bottom: 32px;
  padding: 0 clamp(12px, 3vw, 16px);
`;

const FooterEl = styled.footer`
  max-width: 1700px;
  margin: 0 auto;
  padding: clamp(40px, 5vw, 64px) clamp(24px, 4vw, 56px);
  background: radial-gradient(
      120% 140% at 0% 0%,
      rgba(99, 102, 241, 0.18) 0%,
      rgba(99, 102, 241, 0) 55%
    ),
    linear-gradient(180deg, #0f172a 0%, #16132e 100%);
  color: #cbd5e1;
  border-radius: 28px;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  position: relative;
  overflow: hidden;
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 48px;
  align-items: start;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const Brand = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  .logo {
    width: 168px;
    height: auto;
  }
  .info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13.5px;
    line-height: 1.7;
    color: #94a3b8;
  }
  .info .name {
    font-size: 14px;
    font-weight: 600;
    color: #e2e8f0;
    letter-spacing: 0.04em;
    margin-bottom: 4px;
  }
  .copyright {
    margin-top: 16px;
    font-size: 12px;
    color: #64748b;
    letter-spacing: 0.02em;
  }
  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.15s ease;
  }
  a:hover {
    color: #ffffff;
  }
`;

const Side = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 20px;

  @media (max-width: 820px) {
    align-items: flex-start;
  }
`;

const SideLabel = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #818cf8;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 20px;
  border-radius: 12px;
  border: 1px solid
    ${(p) => (p.$primary ? "transparent" : "rgba(255, 255, 255, 0.14)")};
  background: ${(p) =>
    p.$primary ? "linear-gradient(135deg, #6366f1, #7c3aed)" : "transparent"};
  color: ${(p) => (p.$primary ? "#ffffff" : "#e2e8f0")};
  font-size: 13.5px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease,
    filter 0.15s ease;

  &:hover {
    ${(p) =>
      p.$primary
        ? "filter: brightness(1.08);"
        : `background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.28);
    color: #ffffff;`}
  }
`;

const Socials = styled.div`
  display: flex;
  gap: 10px;
`;

const SocialLink = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  color: #cbd5e1;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;

  &:hover {
    background: rgba(99, 102, 241, 0.22);
    color: #ffffff;
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const IconFacebook = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.49-3.9 3.78-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.25 0-1.64.78-1.64 1.57V12h2.79l-.45 2.89h-2.34v6.99A10 10 0 0 0 22 12z" />
  </svg>
);
const IconTwitter = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
  </svg>
);
const IconInstagram = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);
const IconLinkedIn = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zM8.34 18H5.67V9.67h2.67V18zM7 8.5a1.55 1.55 0 1 1 0-3.1 1.55 1.55 0 0 1 0 3.1zm11 9.5h-2.67v-4.04c0-.96-.02-2.2-1.34-2.2-1.34 0-1.55 1.05-1.55 2.13V18H9.77V9.67h2.56v1.14h.04c.36-.68 1.24-1.4 2.56-1.4 2.74 0 3.25 1.8 3.25 4.14V18z" />
  </svg>
);
const IconArrowRight = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

export function Footer() {
  return (
    <Outer>
      <FooterEl>
        <Inner>
          <Brand>
            <a href="/" aria-label="Youngand home">
              <img
                className="logo"
                src="/images/main_logo2.png"
                alt="Youngand"
              />
            </a>
            <div className="info">
              <span className="name">YOUNGAND</span>
              <span>
                Room 108, I-PLEX Gwangju, 150, Donggyecheon-ro, Dong-gu,
                Gwangju, Republic of Korea
              </span>
              <span>
                <a href="mailto:info@youngandx.com">info@youngandx.com</a>
              </span>
            </div>
            <div className="copyright">
              © {new Date().getFullYear()} YoungAnd. All rights reserved.
            </div>
          </Brand>

          <Side>
            <SideLabel>Get in touch</SideLabel>
            <ActionButtons>
              <ActionLink href="/support/inquiry" $primary>
                Inquiry <IconArrowRight />
              </ActionLink>
              <ActionLink href="/community/hiring">
                Recruitment <IconArrowRight />
              </ActionLink>
            </ActionButtons>
            <Socials>
              <SocialLink href="#" aria-label="Facebook">
                <IconFacebook />
              </SocialLink>
              <SocialLink href="#" aria-label="X (Twitter)">
                <IconTwitter />
              </SocialLink>
              <SocialLink href="#" aria-label="Instagram">
                <IconInstagram />
              </SocialLink>
              <SocialLink href="#" aria-label="LinkedIn">
                <IconLinkedIn />
              </SocialLink>
            </Socials>
          </Side>
        </Inner>
      </FooterEl>
    </Outer>
  );
}
